import { startOfDay } from 'date-fns';

const MINUTES_IN_DAY = 1440;

/** The subset of a calendar event needed to place its arrival cutoff. */
export interface CutoffWindow {
  start: Date;
  end: Date;
  cutoff?: Date | null;
}

/**
 * Vertical distance in pixels from the top of the event block to its arrival
 * cutoff, or null when no marker should be drawn.
 *
 * The offset is relative to the block rather than to the column so it survives
 * `expandRecurringEvents` cloning an occurrence and stays attached to the block
 * while a drag preview moves it.
 *
 * Returns null when the window has no cutoff, when the cutoff falls outside the
 * span visible on `day` (a marker outside its own block would read as belonging
 * to a neighbour), or before the column has been measured.
 */
export function cutoffOffsetPx(
  window: CutoffWindow,
  day: Date,
  contentHeight: number
): number | null {
  if (window.cutoff == null || contentHeight <= 0) {
    return null;
  }

  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart.getTime() + MINUTES_IN_DAY * 60_000);

  // Same clamping as the block's own top/height: a window crossing midnight is
  // cut at the column edges on each day it appears.
  const effectiveStart = window.start < dayStart ? dayStart : window.start;
  const effectiveEnd = window.end > dayEnd ? dayEnd : window.end;

  if (window.cutoff < effectiveStart || window.cutoff > effectiveEnd) {
    return null;
  }

  const minutesFromBlockTop = (window.cutoff.getTime() - effectiveStart.getTime()) / 60_000;

  return (minutesFromBlockTop / MINUTES_IN_DAY) * contentHeight;
}
